import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  Card, 
  CardContent,
  CircularProgress,
  Grid
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  DataUsage as DataUsageIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

function DataLoader() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const loadSampleData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate loading data - in a real app, this would make API calls
      console.log('Loading sample data...');
      
      // Wait a bit to simulate loading
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real implementation, you would dispatch actions like:
      // dispatch(loadStudentsFromAPI());
      // dispatch(loadTeachersFromAPI());
      // dispatch(loadExpensesFromAPI());
      // etc.
      
      console.log('Sample data loaded successfully');
      setLoaded(true);
      
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    } finally {
      setLoading(false);
    }
  };

  const checkDataStatus = () => {
    // This would check the current Redux state
    console.log('Checking data status...');
    // In a real implementation, you would check the Redux store
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Loader
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        This component helps load and verify data for the accounting system.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Load Sample Data
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Click to load sample data for testing the accounting system.
              </Typography>
              
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <DataUsageIcon />}
                onClick={loadSampleData}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? 'Loading...' : 'Load Sample Data'}
              </Button>
              
              {loaded && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  Sample data loaded successfully!
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Check Data Status
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Verify what data is currently available in the system.
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={checkDataStatus}
                sx={{ mb: 2 }}
              >
                Check Status
              </Button>
              
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Note:</strong> In a real application, this would check the Redux store 
                  and display the current data status. For now, check the browser console 
                  for detailed data information.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Troubleshooting Steps
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>Check the browser console for any error messages</li>
              <li>Verify that the backend server is running</li>
              <li>Ensure API endpoints are accessible</li>
              <li>Check Redux DevTools for state updates</li>
              <li>Verify data structure matches expected format</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default DataLoader;
